import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Play, RefreshCw, History, Zap } from "lucide-react";
import { PromptTemplateSelector } from "./PromptTemplateSelector";
import { VersionComparison } from "./VersionComparison";
import { stripMarkdownFormatting } from "@/lib/stripMarkdown";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ExecuteStageProps {
  sprintId: string;
  userId: string;
  onComplete: () => void;
}

export const ExecuteStage = ({ sprintId, userId, onComplete }: ExecuteStageProps) => {
  const { toast } = useToast();
  const [prompt, setPrompt] = useState('');
  const [output, setOutput] = useState('');
  const [executing, setExecuting] = useState(false);
  const [iteration, setIteration] = useState(1);
  const [objective, setObjective] = useState('');
  const [allVersions, setAllVersions] = useState<any[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<number>(0);
  const [showComparison, setShowComparison] = useState(false);

  useEffect(() => {
    loadSprintObjective();
    loadVersionHistory();
  }, [sprintId]);

  const loadVersionHistory = async () => {
    const { data } = await supabase
      .from('sprint_contributions')
      .select('*')
      .eq('sprint_id', sprintId)
      .eq('contributor_id', userId)
      .eq('stage', 'execute')
      .order('iteration_number', { ascending: true });
    
    if (data) setAllVersions(data);
  };

  const loadSprintObjective = async () => {
    const { data } = await supabase
      .from('prompt_architecture_sprints')
      .select('objective')
      .eq('id', sprintId)
      .single();
    
    if (data) setObjective(data.objective);
  };

  useEffect(() => {
    generateInitialPrompt();
  }, [sprintId]);

  const generateInitialPrompt = async () => {
    try {
      const { data: sprint } = await supabase
        .from('prompt_architecture_sprints')
        .select('objective')
        .eq('id', sprintId)
        .single();

      const { data: decisions } = await supabase
        .from('sprint_decisions')
        .select('decision_text, rationale')
        .eq('sprint_id', sprintId)
        .eq('is_selected', true);

      let initialPrompt = `Sprint Objective: ${sprint?.objective}\n\n`;
      
      if (decisions && decisions.length > 0) {
        initialPrompt += `Key Decisions:\n`;
        decisions.forEach(d => {
          initialPrompt += `- ${d.decision_text}\n`;
        });
      }

      initialPrompt += `\nPlease provide a detailed response based on the above objective and decisions.`;
      
      setPrompt(initialPrompt);
    } catch (error: any) {
      console.error('Failed to generate prompt:', error);
    }
  };

  const handleExecute = async () => {
    if (!prompt.trim()) {
      toast({ title: "Please enter a prompt", variant: "destructive" });
      return;
    }

    setExecuting(true);
    setOutput('');

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/execute-sprint-prompt`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({
            prompt,
            sprintId,
            contributorId: userId
          })
        }
      );

      if (!response.ok) throw new Error('Failed to execute prompt');

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulatedOutput = ''; // Accumulate output locally

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          
          // Keep last incomplete line in buffer
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.trim() || line.startsWith(':')) continue;
            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();
              if (data === '[DONE]') continue;
              
              try {
                const parsed = JSON.parse(data);
                const content = parsed.choices?.[0]?.delta?.content;
                if (content) {
                  accumulatedOutput += content;
                  setOutput(accumulatedOutput);
                }
              } catch (e) {
                console.warn('Failed to parse SSE chunk:', data);
              }
            }
          }
        }
      }

      // Save contribution with accumulated output
      await supabase.from('sprint_contributions').insert({
        sprint_id: sprintId,
        contributor_id: userId,
        stage: 'execute',
        contribution_text: accumulatedOutput,
        prompt_used: prompt,
        ai_output: accumulatedOutput,
        iteration_number: iteration
      });

      // Reload versions
      await loadVersionHistory();

      toast({ title: "Prompt executed successfully" });
    } catch (error: any) {
      toast({
        title: "Execution failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setExecuting(false);
    }
  };

  const handleIterate = () => {
    setIteration(prev => prev + 1);
    setOutput('');
    generateInitialPrompt();
    toast({ 
      title: "New iteration started", 
      description: "Prompt reset to baseline. Refine and execute when ready." 
    });
  };

  const handleCompleteStage = async () => {
    try {
      const { error } = await supabase
        .from('prompt_architecture_sprints')
        .update({ execute_stage_completed: true })
        .eq('id', sprintId);

      if (error) throw error;

      onComplete();
    } catch (error: any) {
      toast({
        title: "Failed to complete stage",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-6">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold mb-2">Stage 3: Execute</h3>
              <p className="text-muted-foreground">
                Generate prompts based on your decisions and see AI-powered outputs. Iterate as needed.
              </p>
            </div>
            {allVersions.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowComparison(!showComparison)}
              >
                <History className="w-4 h-4 mr-2" />
                {showComparison ? 'Hide' : 'View'} Versions
              </Button>
            )}
          </div>

          {showComparison && allVersions.length > 1 && (
            <VersionComparison
              currentVersion={{
                iteration: allVersions[allVersions.length - 1].iteration_number,
                prompt: allVersions[allVersions.length - 1].prompt_used,
                aiOutput: allVersions[allVersions.length - 1].ai_output,
                createdAt: allVersions[allVersions.length - 1].created_at
              }}
              previousVersion={{
                iteration: allVersions[allVersions.length - 2].iteration_number,
                prompt: allVersions[allVersions.length - 2].prompt_used,
                aiOutput: allVersions[allVersions.length - 2].ai_output,
                createdAt: allVersions[allVersions.length - 2].created_at
              }}
            />
          )}

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium">Prompt (Iteration {iteration})</label>
                <div className="flex items-center gap-2">
                  <PromptTemplateSelector 
                    onSelectTemplate={setPrompt}
                    objective={objective}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleIterate}
                    disabled={executing}
                    title="Reset prompt to baseline and start a new iteration"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Start New Iteration
                  </Button>
                </div>
              </div>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={12}
                placeholder="Enter your prompt here..."
                disabled={executing}
              />
            </div>

            <Button
              onClick={handleExecute}
              disabled={executing || !prompt.trim()}
              className="w-full"
              size="lg"
            >
              {executing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Executing...
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Execute Prompt
                </>
              )}
            </Button>
          </div>

          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium">AI Output</label>
                {allVersions.length > 0 && (
                  <Select
                    value={selectedVersion.toString()}
                    onValueChange={(v) => {
                      const idx = parseInt(v);
                      setSelectedVersion(idx);
                      setPrompt(allVersions[idx].prompt_used);
                      setOutput(allVersions[idx].ai_output);
                      setIteration(allVersions[idx].iteration_number);
                    }}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Select version" />
                    </SelectTrigger>
                    <SelectContent>
                      {allVersions.map((v, idx) => (
                        <SelectItem key={v.id} value={idx.toString()}>
                          Iteration {v.iteration_number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="border rounded-lg p-4 min-h-[300px] max-h-[500px] overflow-y-auto bg-muted/30">
                {executing && !output && (
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    <Loader2 className="w-6 h-6 animate-spin" />
                  </div>
                )}
                {output && (
                  <div className="prose prose-sm max-w-none dark:prose-invert">
                    <div 
                      className="whitespace-pre-wrap"
                      dangerouslySetInnerHTML={{ __html: stripMarkdownFormatting(output) }}
                    />
                  </div>
                )}
                {!executing && !output && (
                  <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-2">
                    <Zap className="w-12 h-12 opacity-20" />
                    <p className="text-sm">Execute a prompt to see output</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

          <div className="pt-4 border-t">
            <Button
              onClick={handleCompleteStage}
              size="lg"
              className="w-full"
              disabled={!output}
            >
              Complete Execute Stage →
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
